import Icon from '@/components/Icon'
import {
	PrescriptionConfirmationAlert,
	PrescriptionIncompleteAlert,
} from '@/components/prescriptions/PrescriptionStatusAlert'
import {
	AUTHOR_STUDY_LABEL,
	CourseSelections,
	getCourseConfirmationMessage,
	getCourseOptionGroups,
	getCourseStatus,
	getUnmetMessages,
	SELECT_PLACEHOLDER_TEXT,
} from '@/components/prescriptions/utils/prescriptionSelectionRules'
import type { PrescriptionSelectionCourse } from '@/types/fetchPrescriptionSelection'
import { ChangeEvent, useId, useMemo } from 'react'

export interface PrescriptionStudiesAccordionProps {
	course: PrescriptionSelectionCourse
	courseSelections: CourseSelections | undefined
	onSelectionChange: (_slotIndex: number, _workIds: string[]) => void
	onResetCourse: () => void
}

const STUDIES_SLOT_INDEX = 0

export function PrescriptionStudiesAccordion({
	course,
	courseSelections,
	onSelectionChange,
	onResetCourse,
}: PrescriptionStudiesAccordionProps) {
	const reactId = useId()
	const courseTitle = course['course-title']
	const status = getCourseStatus(course, courseSelections)
	const unmetMessages = useMemo(
		() => getUnmetMessages(course, courseSelections),
		[course, courseSelections],
	)

	const studies = getCourseOptionGroups(course)[0]?.studies ?? []
	const focusArea =
		course.selectors?.[0]?.['focus-area-title'] ?? 'Author and authority'

	const currentSlot = courseSelections?.[STUDIES_SLOT_INDEX]
	const currentValue =
		currentSlot && currentSlot.workIds.length > 0
			? currentSlot.workIds[0]
			: ''

	const htmlId = `studies-selector-${reactId}`

	const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value
		onSelectionChange(STUDIES_SLOT_INDEX, value ? [value] : [])
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="overflow-x-auto">
				<table className="w-full border-collapse text-left">
					<thead className="hidden md:table-header-group">
						<tr>
							<th
								scope="col"
								className="font-bold p-3 align-top border-b border-nsw-grey-04 w-2/5"
							>
								Focus area
							</th>
							<th
								scope="col"
								className="font-bold p-3 align-top border-b border-nsw-grey-04"
							>
								Your selection
							</th>
						</tr>
					</thead>
					<tbody>
						<tr className="md:border-b md:border-nsw-grey-04 flex flex-col md:table-row gap-2 md:gap-0 mb-4 md:mb-0">
							<td className="p-3 align-top md:w-2/5">
								<span className="font-bold md:font-normal">
									{focusArea}
								</span>
							</td>
							<td className="p-3 align-top">
								<p className="font-bold mt-0 mb-2">
									{AUTHOR_STUDY_LABEL}
								</p>
								<div className="relative w-full">
									<select
										id={htmlId}
										value={currentValue}
										onChange={handleChange}
										className="nsw-form__select w-full !border-nsw-grey-04"
										style={{ backgroundImage: 'none' }}
										aria-label={`Select author study for ${courseTitle}`}
										data-testid={htmlId}
									>
										<option value="">
											{SELECT_PLACEHOLDER_TEXT}
										</option>
										{studies.map((study) => (
											<option
												key={study.id}
												value={study.id}
											>
												{study.author}
											</option>
										))}
									</select>
									<Icon
										icon="ic:baseline-keyboard-arrow-down"
										width={24}
										height={24}
										aria-hidden="true"
										className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-nsw-grey-01"
									/>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			{status !== 'complete' && (
				<PrescriptionIncompleteAlert messages={unmetMessages} />
			)}

			{status === 'complete' && (
				<PrescriptionConfirmationAlert
					message={getCourseConfirmationMessage(course)}
					courseTitle={courseTitle}
				/>
			)}

			<div className="flex justify-end">
				<button
					type="button"
					className="underline font-bold nsw-text--brand-dark"
					onClick={onResetCourse}
					data-testid="prescription-reset-course"
				>
					Reset selections
				</button>
			</div>
		</div>
	)
}

export default PrescriptionStudiesAccordion
